/**
 * Utility functions for managing question hierarchy, numbering, and ordering
 * This module provides a hybrid approach: flat array storage with tree-based operations
 */

export interface Question {
  id: string;
  question: string;
  question_type: 'free_text' | 'single_choice' | 'multiple_choice';
  response_choices: string[];
  comments_allowed: boolean;
  is_dropdown: boolean;
  required: boolean;
  parent_question_id?: string;
  parent_question_response_option?: string | null;
  parent_question_response_option_index?: number | null; // Index of the option (0, 1, 2...) for unique identification
  question_number?: string;
}

export interface QuestionTreeNode {
  id: string;
  displayNumber: string;
  children: Map<string | null, QuestionTreeNode[]>; // key is response option or null for text fields
  question: Question;
}

/**
 * Builds a tree structure from flat array of questions
 * Root questions are those without parent_question_id
 */
export function buildQuestionTree(questions: Question[]): QuestionTreeNode[] {
  const questionMap = new Map<string, Question>();
  const rootNodes: QuestionTreeNode[] = [];
  const nodeMap = new Map<string, QuestionTreeNode>();

  // Create map for quick lookup
  for (const question of questions) {
    questionMap.set(question.id, question);
  }

  // Helper to create a node
  const createNode = (question: Question): QuestionTreeNode => {
    if (nodeMap.has(question.id)) {
      return nodeMap.get(question.id)!;
    }

    const node: QuestionTreeNode = {
      id: question.id,
      displayNumber: '', // Will be calculated later
      children: new Map(),
      question,
    };

    nodeMap.set(question.id, node);
    return node;
  };

  // Build tree structure
  for (const question of questions) {
    const node = createNode(question);

    if (!question.parent_question_id) {
      // Root question
      rootNodes.push(node);
    } else {
      // Child question - attach to parent
      const parent = questionMap.get(question.parent_question_id);
      if (parent) {
        const parentNode = createNode(parent);
        // Use option index as key instead of option text to handle duplicate option values
        // For text fields, parent_question_response_option_index is null
        const optionKey = question.parent_question_response_option_index !== null &&
                         question.parent_question_response_option_index !== undefined
          ? String(question.parent_question_response_option_index)
          : null;

        if (!parentNode.children.has(optionKey)) {
          parentNode.children.set(optionKey, []);
        }
        parentNode.children.get(optionKey)!.push(node);
      }
    }
  }

  // Calculate display numbers
  rootNodes.forEach((node, index) => {
    calculateDisplayNumbers(node, String(index + 1));
  });

  return rootNodes;
}

/**
 * Recursively calculates display numbers for a tree node and its children
 */
function calculateDisplayNumbers(node: QuestionTreeNode, number: string): void {
  node.displayNumber = number;

  // Get all option keys and sort them
  const optionKeys = Array.from(node.children.keys()).sort((a, b) => {
    // null (text field) comes first
    if (a === null) return -1;
    if (b === null) return 1;
    // Sort numeric string keys by their numeric value
    return Number(a) - Number(b);
  });

  // Assign sequential display numbers (a, b, c...) regardless of option index
  let childCounter = 0;

  // Process children in sorted order by option index
  optionKeys.forEach((optionKey) => {
    const childNodes = node.children.get(optionKey)!;

    if (optionKey === null) {
      // Text field - use ".a" for the child
      childNodes.forEach((child, idx) => {
        if (idx === 0) {
          calculateDisplayNumbers(child, `${number}.a`);
          childCounter++;
        } else {
          // If multiple children for text field (shouldn't happen), use .a, .b, .c
          const suffix = String.fromCharCode(97 + childCounter);
          calculateDisplayNumbers(child, `${number}.${suffix}`);
          childCounter++;
        }
      });
    } else {
      // Option-based (single/multiple choice)
      // Assign sequential letters (a, b, c...) based on order, not option index
      childNodes.forEach((child) => {
        const suffix = String.fromCharCode(97 + childCounter); // a, b, c, ...
        calculateDisplayNumbers(child, `${number}.${suffix}`);
        childCounter++;
      });
    }
  });
}

/**
 * Flattens tree back to ordered array of question IDs
 * Maintains parent-child hierarchy in the order
 */
export function flattenTreeToOrderedList(tree: QuestionTreeNode[]): string[] {
  const result: string[] = [];

  function traverse(node: QuestionTreeNode) {
    result.push(node.id);

    // Traverse children in order of options
    const sortedOptions = Array.from(node.children.keys()).sort((a, b) => {
      if (a === null) return -1; // Text field children come first
      if (b === null) return 1;

      // Sort by option index in parent's response_choices
      const indexA = node.question.response_choices.indexOf(a);
      const indexB = node.question.response_choices.indexOf(b);
      return indexA - indexB;
    });

    for (const optionKey of sortedOptions) {
      const children = node.children.get(optionKey)!;
      for (const child of children) {
        traverse(child);
      }
    }
  }

  for (const rootNode of tree) {
    traverse(rootNode);
  }

  return result;
}

/**
 * Checks if mapping fromQuestionId to toQuestionId would create a cycle
 * Returns true if cycle would be created
 */
export function wouldCreateCycle(
  questions: Question[],
  fromQuestionId: string,
  toQuestionId: string
): boolean {
  // Build a map of parent relationships
  const parentMap = new Map<string, string>();

  for (const q of questions) {
    if (q.parent_question_id) {
      parentMap.set(q.id, q.parent_question_id);
    }
  }

  // Simulate adding the new relationship
  parentMap.set(toQuestionId, fromQuestionId);

  // Check for cycle using DFS
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function hasCycleDFS(nodeId: string): boolean {
    if (recursionStack.has(nodeId)) {
      return true; // Cycle detected
    }

    if (visited.has(nodeId)) {
      return false; // Already processed, no cycle from here
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const parentId = parentMap.get(nodeId);
    if (parentId && hasCycleDFS(parentId)) {
      return true;
    }

    recursionStack.delete(nodeId);
    return false;
  }

  return hasCycleDFS(toQuestionId);
}

/**
 * Checks if a question is already mapped as a sub-question
 */
export function isAlreadyMapped(question: Question): boolean {
  return !!question.parent_question_id;
}

/**
 * Gets all descendant question IDs for a given question
 */
export function getDescendantIds(questions: Question[], questionId: string): Set<string> {
  const descendants = new Set<string>();
  const childrenMap = new Map<string, string[]>();

  // Build children map
  for (const q of questions) {
    if (q.parent_question_id) {
      if (!childrenMap.has(q.parent_question_id)) {
        childrenMap.set(q.parent_question_id, []);
      }
      childrenMap.get(q.parent_question_id)!.push(q.id);
    }
  }

  // DFS to find all descendants
  function collectDescendants(id: string) {
    const children = childrenMap.get(id) || [];
    for (const childId of children) {
      descendants.add(childId);
      collectDescendants(childId);
    }
  }

  collectDescendants(questionId);
  return descendants;
}

/**
 * Sorts children of a parent question by their option order
 * Ensures children appear in order: 1.a, 1.b, 1.c (not 1.c, 1.a, 1.b)
 */
export function sortChildrenByOptionOrder(questions: Question[], parentQuestionId: string): Question[] {
  const parent = questions.find((q) => q.id === parentQuestionId);
  if (!parent) return questions;

  // Get all direct children of this parent
  const children = questions.filter((q) => q.parent_question_id === parentQuestionId);

  // Sort children by their option index
  const sortedChildren = children.sort((a, b) => {
    // Handle text field children (null option index)
    if (a.parent_question_response_option_index === null || a.parent_question_response_option_index === undefined) return -1;
    if (b.parent_question_response_option_index === null || b.parent_question_response_option_index === undefined) return 1;

    // Compare by option index directly (no need to search in response_choices array)
    return a.parent_question_response_option_index - b.parent_question_response_option_index;
  });

  // Build a map of sorted positions
  const childPositionMap = new Map<string, number>();
  sortedChildren.forEach((child, index) => {
    childPositionMap.set(child.id, index);
  });

  // Find where to insert sorted children (right after parent)
  const parentIndex = questions.findIndex((q) => q.id === parentQuestionId);

  // Collect all child IDs (including their descendants)
  const allChildIds = new Set<string>();

  function collectAllDescendants(questionId: string) {
    allChildIds.add(questionId);
    for (const q of questions) {
      if (q.parent_question_id === questionId) {
        collectAllDescendants(q.id);
      }
    }
  }

  for (const child of children) {
    collectAllDescendants(child.id);
  }

  // Split into: before parent, parent, sorted children with subtrees, after
  const beforeParent: Question[] = [];
  const sortedChildrenWithSubtrees: Question[][] = sortedChildren.map(() => []);
  const afterChildren: Question[] = [];

  let passedParent = false;
  let passedAllChildren = false;

  for (const q of questions) {
    if (q.id === parentQuestionId) {
      beforeParent.push(q);
      passedParent = true;
    } else if (allChildIds.has(q.id)) {
      // Find which sorted child this belongs to
      let rootChild = q;
      while (rootChild.parent_question_id !== parentQuestionId && rootChild.parent_question_id) {
        rootChild = questions.find((x) => x.id === rootChild.parent_question_id)!;
      }

      const position = childPositionMap.get(rootChild.id);
      if (position !== undefined) {
        sortedChildrenWithSubtrees[position].push(q);
      }
    } else if (!passedParent) {
      beforeParent.push(q);
    } else {
      afterChildren.push(q);
    }
  }

  // Flatten sorted children with subtrees
  const flattenedSortedChildren = sortedChildrenWithSubtrees.flat();

  return [...beforeParent, ...flattenedSortedChildren, ...afterChildren];
}

/**
 * Reorders questions array after a parent-child mapping
 * When a child question appears before its parent, the child is moved to appear immediately after the parent
 * Also sorts all children by their option order (a, b, c)
 */
export function reorderQuestionsAfterMapping(
  questions: Question[],
  parentQuestionId: string,
  childQuestionId: string
): Question[] {
  const parentIndex = questions.findIndex((q) => q.id === parentQuestionId);
  const childIndex = questions.findIndex((q) => q.id === childQuestionId);

  let reorderedQuestions = [...questions];

  // Step 1: If child comes before parent, move child after parent
  if (childIndex < parentIndex) {
    // Get all descendants of the child (in case child already has sub-questions)
    const childSubtreeIds = new Set<string>([childQuestionId]);

    function collectChildSubtree(questionId: string) {
      for (const q of reorderedQuestions) {
        if (q.parent_question_id === questionId) {
          childSubtreeIds.add(q.id);
          collectChildSubtree(q.id);
        }
      }
    }

    collectChildSubtree(childQuestionId);

    // Split questions into three groups
    const upToParent: Question[] = [];
    const childSubtree: Question[] = [];
    const afterParent: Question[] = [];

    let passedParent = false;

    for (const q of reorderedQuestions) {
      if (childSubtreeIds.has(q.id)) {
        childSubtree.push(q);
      } else if (q.id === parentQuestionId) {
        upToParent.push(q);
        passedParent = true;
      } else if (!passedParent) {
        upToParent.push(q);
      } else {
        afterParent.push(q);
      }
    }

    reorderedQuestions = [...upToParent, ...childSubtree, ...afterParent];
  }

  // Step 2: Sort all children of the parent by option order
  reorderedQuestions = sortChildrenByOptionOrder(reorderedQuestions, parentQuestionId);

  return reorderedQuestions;
}

/**
 * Gets the display number for a specific question from the tree
 */
export function getQuestionDisplayNumber(questions: Question[], questionId: string): string {
  const tree = buildQuestionTree(questions);

  function findDisplayNumber(nodes: QuestionTreeNode[]): string | null {
    for (const node of nodes) {
      if (node.id === questionId) {
        return node.displayNumber;
      }

      for (const children of node.children.values()) {
        const found = findDisplayNumber(children);
        if (found) return found;
      }
    }
    return null;
  }

  return findDisplayNumber(tree) || '?';
}

/**
 * Gets available questions for sub-question mapping
 * Excludes: current question, already mapped questions, descendants, and questions that would create cycles
 */
export function getAvailableSubQuestions(
  questions: Question[],
  currentQuestionId: string
): Question[] {
  const descendants = getDescendantIds(questions, currentQuestionId);

  return questions.filter((q) => {
    // Exclude current question
    if (q.id === currentQuestionId) return false;

    // Exclude already mapped questions
    if (isAlreadyMapped(q)) return false;

    // Exclude descendants
    if (descendants.has(q.id)) return false;

    // Exclude if would create cycle
    if (wouldCreateCycle(questions, currentQuestionId, q.id)) return false;

    return true;
  });
}

/**
 * Converts children to root questions when a parent is deleted
 */
export function convertChildrenToRootQuestions(questions: Question[], deletedParentId: string): Question[] {
  return questions.map((q) => {
    if (q.parent_question_id === deletedParentId) {
      return {
        ...q,
        parent_question_id: undefined,
        parent_question_response_option_index: undefined,
      };
    }
    return q;
  });
}

/**
 * Moves an unmapped question (and its subtree) to after all siblings of its former parent
 * When Q1.b is unmapped, it should move to after Q1.c (after all of Q1's children)
 */
export function moveUnmappedQuestionAfterSiblings(
  questions: Question[],
  unmappedQuestionId: string,
  formerParentId: string
): Question[] {
  // Find all children of the former parent (these are the siblings)
  const siblings = questions.filter((q) => q.parent_question_id === formerParentId);

  if (siblings.length === 0) {
    // No siblings, just return as is
    return questions;
  }

  // Find the last sibling and all its descendants
  const lastSibling = siblings[siblings.length - 1];
  const lastSiblingDescendants = getDescendantIds(questions, lastSibling.id);

  // Find the position after the last sibling and all its descendants
  let insertAfterIndex = questions.findIndex((q) => q.id === lastSibling.id);

  // Find the last descendant position
  for (let i = insertAfterIndex + 1; i < questions.length; i++) {
    if (lastSiblingDescendants.has(questions[i].id)) {
      insertAfterIndex = i;
    } else {
      break;
    }
  }

  // Collect the unmapped question and its subtree
  const unmappedSubtreeIds = new Set<string>([unmappedQuestionId]);

  function collectSubtree(questionId: string) {
    for (const q of questions) {
      if (q.parent_question_id === questionId) {
        unmappedSubtreeIds.add(q.id);
        collectSubtree(q.id);
      }
    }
  }

  collectSubtree(unmappedQuestionId);

  // Split into three groups: before, unmapped subtree, after
  const beforeInsertion: Question[] = [];
  const unmappedSubtree: Question[] = [];
  const afterInsertion: Question[] = [];

  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];

    if (unmappedSubtreeIds.has(q.id)) {
      unmappedSubtree.push(q);
    } else if (i <= insertAfterIndex) {
      beforeInsertion.push(q);
    } else {
      afterInsertion.push(q);
    }
  }

  return [...beforeInsertion, ...unmappedSubtree, ...afterInsertion];
}

/**
 * Moves a question and its entire subtree when dragging
 * Ensures parent and all children move together
 */
export function moveQuestionSubtree(
  questions: Question[],
  draggedQuestionId: string,
  targetQuestionId: string
): Question[] {
  // Find indices
  const draggedIndex = questions.findIndex((q) => q.id === draggedQuestionId);
  const targetIndex = questions.findIndex((q) => q.id === targetQuestionId);

  if (draggedIndex === -1 || targetIndex === -1) return questions;

  // If dragging to same position, no change
  if (draggedIndex === targetIndex) return questions;

  // Collect all descendants of the dragged question (the subtree to move)
  const subtreeIds = new Set<string>([draggedQuestionId]);

  function collectSubtree(questionId: string) {
    for (const q of questions) {
      if (q.parent_question_id === questionId) {
        subtreeIds.add(q.id);
        collectSubtree(q.id);
      }
    }
  }

  collectSubtree(draggedQuestionId);

  // Extract the subtree and remaining questions
  const subtree: Question[] = [];
  const remaining: Question[] = [];

  for (const q of questions) {
    if (subtreeIds.has(q.id)) {
      subtree.push(q);
    } else {
      remaining.push(q);
    }
  }

  // Find where the target question ended up in the remaining array
  const targetIndexInRemaining = remaining.findIndex((q) => q.id === targetQuestionId);

  if (targetIndexInRemaining === -1) {
    // Target was in the subtree being moved, shouldn't happen but return original
    return questions;
  }

  // Determine insert position based on drag direction
  let insertPosition: number;

  if (draggedIndex < targetIndex) {
    // Dragging down - insert after target
    insertPosition = targetIndexInRemaining + 1;
  } else {
    // Dragging up - insert before target
    insertPosition = targetIndexInRemaining;
  }

  // Insert subtree at the calculated position
  const result = [
    ...remaining.slice(0, insertPosition),
    ...subtree,
    ...remaining.slice(insertPosition),
  ];

  return result;
}

/**
 * Gets formatted display text for a question in dropdowns
 * Shows question number and text for easy identification
 * Note: Truncation is handled by CSS (overflow-hidden text-ellipsis) in the UI component
 */
export function getQuestionDisplayText(questions: Question[], question: Question, isMapped: boolean): string {
  const displayNumber = getQuestionDisplayNumber(questions, question.id);
  const questionText = question.question?.trim() || '';

  if (isMapped && questionText) {
    return questionText;
  }

  if (questionText) {
    return `Question ${displayNumber}: ${questionText}`;
  }

  // If no question text, show only the display number
  return `Question ${displayNumber}`;
}

/**
 * Deduplicate response_choices by trimming whitespace and removing duplicates
 * @param choices - Array of choice strings that may contain duplicates or whitespace
 * @returns Array of unique, trimmed, non-empty strings
 * @example
 * deduplicateResponseChoices(["yes  ", "yes", " no ", "no", ""]) // ["yes", "no"]
 */
export function deduplicateResponseChoices(choices: string[] | undefined): string[] {
  if (!choices || !Array.isArray(choices)) {
    return [];
  }
  const trimmedChoices = choices.map(choice => typeof choice === 'string' ? choice.trim() : '');
  return Array.from(new Set(trimmedChoices)).filter(choice => choice !== '');
}

/**
 * Finds duplicate option indices in a question's response choices (case-sensitive)
 * Returns indices of options that are duplicates of earlier options
 * @param options - Array of option strings
 * @returns Set of indices that are duplicates (excludes the first occurrence)
 * @example
 * findDuplicateOptionIndices(["Yes", "No", "Yes", "no", "Yes", "no"])
 * // Returns Set(3) { 2, 4, 5 } - indices 2, 4, 5 are duplicates
 */
export function findDuplicateOptionIndices(options: string[]): Set<number> {
  const duplicateIndices = new Set<number>();
  const seenOptions = new Map<string, number>(); // trimmed text -> first index

  options.forEach((option, index) => {
    const trimmed = option.trim();

    // Skip empty options (they have their own validation)
    if (trimmed === '') return;

    if (seenOptions.has(trimmed)) {
      // This is a duplicate - mark this index
      duplicateIndices.add(index);
    } else {
      // This is the first occurrence - record it
      seenOptions.set(trimmed, index);
    }
  });

  return duplicateIndices;
}
